import {
  Component,
  inject,
  Inject,
  Input,
  makeStateKey,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ButtonType } from '../../../hdi-components/src/shared/enums/button-type.enums';
import TeaserType from '../../../hdi-components/src/shared/enums/teaser.type.enums';
import ViewportType from '../../../hdi-components/src/shared/enums/viewport-type.enums';

import MenuItem, {
  FlyoutData,
} from '../../../hdi-components/src/shared/interfaces/menuItems.interface';
import {
  CaasFooter,
  CaasLayout,
} from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { PodcastItems } from '../../../hdi-components/src/shared/interfaces/podcast-item.interface';
import { CaasContentService } from '../services/caas-content.service';
import { NavigationService } from '../services/navigation.service';
import { LoadingService } from '../services/loading.service';
import { Video } from '../../../hdi-components/src/shared/interfaces/video.interface';
import { ModalType } from 'hdi-components';
import { Router, Scroll } from '@angular/router';
import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { environment } from '../environments/environment';

const DATA_KEY_LOADING = makeStateKey<boolean>('loading');

declare global {
  interface Window {
    TPP_SNAP: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'hdi-bancassurance';

  @Input() viewportDTM: ViewportType = ViewportType.desktop;
  ModalType = ModalType;

  video?: Video;
  showModal: boolean = false;

  private readonly destroy$ = new Subject<void>();
  private readonly boundVideoHandler = this.handleVideoModal.bind(this);

  visibleTeasers = 3;
  buttonType = ButtonType;
  teaserType = TeaserType;

  // Data holders
  podcasts: PodcastItems[] = [];
  // 642697 09.02.2026 lr removed meta navigation
  // metaMenu: MenuItem[] = [];
  flyoutData: FlyoutData[] = [];
  mainMenu: MenuItem[] = [];
  datasets: CaasLayout[] = [];
  footer: CaasFooter = {} as CaasFooter;
  media: CaasLayout[] = [];
  isLoading = false;
  isLoadingPlaceholder = true;
  loadingSubscription?: Subscription;

  private readonly document = inject(DOCUMENT);

  constructor(
    private readonly caasContentServices: CaasContentService,
    private readonly navigationServices: NavigationService,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly viewportScroller: ViewportScroller,
    private readonly transferState: TransferState,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.loadData();
    this.initEventListeners();
    // Auf Browser: Fetch Loading State
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = this.transferState.get(DATA_KEY_LOADING, false);
    }
    this.addHeaderTags();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingSubscription?.unsubscribe();
    this.removeEventListeners();
  }

  private addHeaderTags(): void {
    if (
      typeof this.document !== 'undefined' &&
      environment.publicSnapUrl &&
      environment.preview &&
      !this.document.defaultView!.TPP_SNAP
    ) {
      const el = this.document.createElement('script');
      el.setAttribute('src', environment.publicSnapUrl);
      this.document.head.appendChild(el);
    } else {
      console.log('Snap URL is not defined or not in preview mode');
    }
  }

  private initEventListeners(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('openVideoModal', this.boundVideoHandler);
    }
  }

  private removeEventListeners(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('openVideoModal', this.boundVideoHandler);
    }
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(filter((event): event is Scroll => event instanceof Scroll))
      .subscribe(this.handleScrollEvent.bind(this));
  }

  private handleScrollEvent(event: Scroll): void {
    setTimeout(() => {
      if (event.position) {
        this.viewportScroller.scrollToPosition(event.position);
      } else if (event.anchor) {
        this.viewportScroller.scrollToAnchor(event.anchor);
      } else {
        this.viewportScroller.scrollToPosition([0, 0]);
      }
    }, 300);
  }

  private subscribeToLoadingState(): void {
    this.loadingSubscription = this.loadingService.loading$.subscribe(
      (loading: boolean) => {
        this.isLoading = loading;
        // Auf Server: Loading State for client speichern
        if (!isPlatformBrowser(this.platformId)) {
          this.transferState.set(DATA_KEY_LOADING, loading);
        }
        if (!loading) {
          this.subscribeToRouterEvents();
        }
      }
    );
  }

  private loadData(): void {
    this.navigationServices
      .getMainNavigation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: MenuItem[]) => (this.mainMenu = data),
        error: (error) => this.logError('Main Navigation', error),
      });

    this.navigationServices
      .getFlyoutData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: FlyoutData[]) => (this.flyoutData = data),
        error: (error) => this.logError('Flyout', error),
      });

    // 642697 09.02.2026 lr removed meta navigation
    /* this.caasContentServices
      .getMetaNavigation()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: MenuItem[]) => (this.metaMenu = data),
        error: (error) => this.logError('Meta Navigation', error),
      });*/

    this.caasContentServices
      .getFooter()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (content: CaasFooter | null) =>
          (this.footer = content as CaasFooter),
        error: (error) => this.logError('Footer Content', error),
      });

    this.caasContentServices
      .getDataset()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CaasLayout[]) => (this.datasets = data),
        error: (error) => this.logError('Dataset Content', error),
      });

    this.caasContentServices
      .getMedia()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: CaasLayout[]) => (this.media = data),
        error: (error) => this.logError('Media Content', error),
      });
  }

  private handleVideoModal(event: Event): void {
    if (event instanceof CustomEvent) {
      this.video = event.detail;
      this.showModal = true;
    }
  }

  private logError(context: string, error: unknown): void {
    console.error(`Error fetching ${context}:`, error);
  }

  public onComponentLoaded() {
    this.isLoadingPlaceholder = false;
  }

  closeModal(event: boolean): void {
    this.showModal = event;
  }
}
